import { type BaseDocumentType } from "~/graphql/supergraphTypes";

/**
 * Tax document base types that require special delete confirmation modal
 * showing affected positions.
 */
export const TAX_DOCUMENT_BASE_TYPES = ["w_series_tax_form", "self_certification"] as const;

export type TaxDocumentBaseType = (typeof TAX_DOCUMENT_BASE_TYPES)[number];

/**
 * Check if a document base type is a tax document (FATCA or CRS)
 */
export const isTaxDocument = (baseType: string | undefined): boolean => {
  if (!baseType) return false;
  return TAX_DOCUMENT_BASE_TYPES.includes(baseType as TaxDocumentBaseType);
};

/**
 * Determine if the special tax document delete modal should be shown.
 *
 * Conditions:
 * - FATCA/CRS is enabled for the current case (callers should derive this from
 *   useIsFatcaCrsEnabledForCase, which already gates by FATCA-relevant case types)
 * - Document belongs to the root/investing profile
 * - Document is a tax document type (W-series or Self-Certification)
 */
export type ShouldShowTaxDeleteModalParams = {
  isFatcaCrsEnabled: boolean;
  documentProfileId: number | null | undefined;
  investingProfileId: number | undefined;
  documentBaseType: BaseDocumentType | undefined;
};

export const shouldShowTaxDeleteModal = ({
  isFatcaCrsEnabled,
  documentProfileId,
  investingProfileId,
  documentBaseType,
}: ShouldShowTaxDeleteModalParams): boolean => {
  if (!isFatcaCrsEnabled) return false;

  const isRootProfileDocument =
    documentProfileId != null && investingProfileId === documentProfileId;
  if (!isRootProfileDocument) return false;

  return isTaxDocument(documentBaseType);
};

/**
 * Get the source type label for display (FATCA or CRS)
 */
export const getSourceTypeLabel = (documentType: BaseDocumentType): "FATCA" | "CRS" => {
  return documentType === "w_series_tax_form" ? "FATCA" : "CRS";
};
